import store from 'src/redux/config/configureStore';
import {
  Entity,
  EntityStatusEnum,
  EntityType,
  Fund,
  Investment,
  InvestmentAmount,
  InvestmentGraphql,
  InvestmentHoldingGraphQL,
  InvestmentRefEnum,
  Project,
  StatusCategoryFilterEnum,
} from 'src/types.d';
import { convertDateToStringFormat } from 'src/utils/functions/dateServices';

export function groupInvestments(investments: InvestmentGraphql[]): {
  [id: string]: InvestmentGraphql[];
} {
  return investments.reduce((acc, investment) => {
    const { id } = investment;
    if (!acc.hasOwnProperty(id)) {
      acc[id] = [];
    }
    acc[id].push(investment);
    return acc;
  }, {});
}

export function getOrganizationDetails(investment: Investment): Entity {
  //validate the investment
  if (!investment) return null;
  const dict = {
    [InvestmentRefEnum.fundId]: (): Fund => investment?.fundDetails as Fund,
    [InvestmentRefEnum.projectId]: (): Project => investment?.projectDetails as Project,
  };
  return dict[investment.ref[0].key]();
}

export function getEmailStringFromInvestment(investment: Investment | InvestmentGraphql): string {
  return investment?.email?.length
    ? investment.email.length === 1
      ? investment.email[0]
      : investment.email.join(', ')
    : '';
}

export function convertInvestmentsGraphqlToInvestments(
  investments: InvestmentGraphql[],
): Investment[] {
  const groupInvestments = doGroupInvestments(investments);
  return Object.values(groupInvestments).map(x => convertInvestmentGraphqlToInvestment(x));
}

export function doGroupInvestments(investments: InvestmentGraphql[]): {
  [key: string]: InvestmentGraphql[];
} {
  return investments.reduce((acc, investment) => {
    let entity;
    const ref = investment?.ref ? investment.ref[0] : null;
    let unique = `${investment?.email}-${investment?.legalEntityId}`;
    if (ref) {
      unique += `-${ref.value}`;
    } else {
      entity = getOrganizationDetails(investment);
      if (entity) {
        unique += `-${entity.id}`;
      }
    }
    if (!acc.hasOwnProperty(unique)) {
      acc[unique] = [];
    }
    acc[unique].push(investment);
    return acc;
  }, {});
}

export function convertInvestmentGraphqlToInvestment(investments: InvestmentGraphql[]): Investment {
  let investmentHoldings = [];
  let investmentManHoldings = [];
  const contributionIdx = investments.findIndex(investment => investment?.isContribution);

  const investmentAmount: InvestmentAmount[] = investments.reduce(
    (acc, investment): InvestmentAmount[] => {
      if (investment.holdings) {
        investmentHoldings = [...investmentHoldings, ...investment.holdings];
      }
      if (investment.manualHoldings) {
        investmentManHoldings = [...investmentManHoldings, ...investment.manualHoldings];
      }

      //filter out holdings that have the same date as in manual holdings
      investmentHoldings = investmentHoldings.filter(
        holding =>
          !investmentManHoldings.some(
            manHolding => convertDateToStringFormat(manHolding.date) === holding.date,
          ),
      );

      const date = investment?.date;
      const exitDate = investment?.exitDate;
      const isContribution = investment?.isContribution;
      const currentDate = new Date();

      //if the investment is in the future disregarding the investment
      if (date && convertDateToStringFormat(currentDate) < convertDateToStringFormat(date)) {
        return [...acc];
      }

      //if the investment is after the project endDate then disregard
      if (
        exitDate &&
        contributionIdx > -1 &&
        convertDateToStringFormat(date) > convertDateToStringFormat(exitDate)
      ) {
        return [...acc];
      }

      const contribution = investment?.amount;
      const percentage = investment?.percentage;
      return [...acc, { date, value: contribution, percentage, isContribution }];
    },
    [],
  );

  const investmentDetails = investments[0];
  if (investmentHoldings.length > 0 || investmentManHoldings.length > 0) {
    return {
      ...investmentDetails,
      investmentAmount,
      holdings: investmentHoldings,
      manualHoldings: investmentManHoldings,
    };
  }

  return { ...investmentDetails, investmentAmount };
}

//todo nir
/**
 * @description filters the investments array by the status
 * @param investments investments array
 * @param status status to filter by
 * @returns filtered investments array
 */
export function filterInvestmentsByStatus(investments: Investment[], status: String): Investment[] {
  const statusLower: string = status?.toLowerCase();

  //the status to filter by
  let statusToFilterBy = null;
  switch (statusLower) {
    case 'active':
      statusToFilterBy = EntityStatusEnum.STARTED;
      break;
    case 'realized':
      statusToFilterBy = EntityStatusEnum.COMPLETED;
      break;
    case 'cancelled':
      statusToFilterBy = EntityStatusEnum.CANCELLED;
      break;
  }

  //if there is no status to filter by return all the investments
  if (!statusToFilterBy) {
    return investments;
  }

  return investments?.filter(
    (investment: Investment) =>
      investment?.projectDetails?.status === statusToFilterBy ||
      investment?.fundDetails?.status === statusToFilterBy,
  );
}

/**
 * Gets the investments related to the current entity view
 * @param investments All investments
 * @returns Investments related to the current entity view
 */
export function getInvestmentByCurrentView(investments: Investment[]): Investment[] {
  //get the current entity view from redux store
  const { investor, entityView, operatorFilterId, categoryFilter } = store.getState().investor;

  const deceasedList =
    investor?.userLegalEntities?.filter(entity => entity.deceased)?.map(entity => entity.id) ?? [];

  //get the filtered investments by the chosen operator
  const matchingOperatorInvestment: Investment[] = investments?.filter(
    investment =>
      investment?.operatorDetails?.id === operatorFilterId &&
      deceasedList?.includes(investment?.legalEntityId) === false,
  );

  //no need to filter
  if (entityView.name === 'All' && categoryFilter === StatusCategoryFilterEnum.ALL)
    return matchingOperatorInvestment;

  // get the investments by the category
  const matchingInvestmentByCategory =
    filterInvestmentsByStatus(matchingOperatorInvestment, categoryFilter) ?? [];

  //filtering only by the category
  if (entityView.name === 'All') return matchingInvestmentByCategory;

  //filtering by the entity view value
  return matchingInvestmentByCategory?.filter(
    investment => investment.legalEntityId === entityView.id,
  );
}

export function convertInvestmentsToGraphqlInvestments(
  investments: Investment[],
  operatorId: string,
  toDelete?: boolean,
): InvestmentGraphql[] {
  return investments.reduce(
    (acc, investment) => [
      ...acc,
      ...convertInvestmentToGraphqlInvestment(investment, operatorId, toDelete),
    ],
    [],
  );
}

export function convertInvestmentToGraphqlInvestment(
  investment: Investment,
  operatorId: string,
  toDelete?: boolean,
): InvestmentGraphql[] | { id: string }[] {
  return toDelete
    ? [{ id: investment?.id }]
    : investment?.investmentAmount.map(investmentAmount => {
        const newInvestment = {
          ...investment,
          operatorId,
          email: investment.email,
          ref: investment.ref,
          percentage: investmentAmount.percentage,
          amount: investmentAmount.value,
          date: investmentAmount.date,
          investedBy: investment.investedBy,
        };

        const entity = investment?.projectDetails
          ? investment.projectDetails
          : investment?.fundDetails
          ? investment.fundDetails
          : null;

        if (entity) {
          if (newInvestment.ref[0].key === InvestmentRefEnum.projectId) {
            newInvestment['projectDetails'] = entity as Project;
          } else {
            newInvestment['fundDetails'] = entity as Fund;
          }
        }

        delete newInvestment['investmentAmount'];
        delete newInvestment['investmentPercentage'];

        return newInvestment;
      });
}

/**
 * Gets the last distribution from the investment holdings
 * @param investment Given investments with holdings
 * @returns Last distribution from the investment holdings
 */
export function getLastHolding(investment: Investment): InvestmentHoldingGraphQL | null {
  const { holdings, manualHoldings } = investment;

  //get the sorted combined holdings
  let combinedHoldings = [...holdings, ...manualHoldings];
  combinedHoldings = combinedHoldings?.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  //return the last date
  if (combinedHoldings?.length) {
    const lastDistribution = combinedHoldings[combinedHoldings?.length - 1];

    return lastDistribution.date ? lastDistribution : null;
  }
  return null;
}

/**
 * Merges the entity details for the given investments
 * @param investments Given investments
 * @returns Investments with merged entity details
 */
export const mergeEntityDetailsForInvestments = (investments: Investment[]): Investment[] => {
  //filter all investments with fundDetails
  let investmentByEntity: Investment[] =
    investments?.map(investment => {
      const entityType: EntityType = investment.projectDetails
        ? EntityType.PROJECT
        : EntityType.FUND;
      const entityDetails: any = investment.projectDetails
        ? investment.projectDetails
        : investment.fundDetails;
      return { ...investment, entityType, entityDetails };
    }) ?? [];

  //sort by funds and then by projects as first level and by name as second level
  return investmentByEntity.sort((a, b) => {
    if (a.entityType === EntityType.FUND && b.entityType === EntityType.PROJECT) return -1;
    if (a.entityType === EntityType.PROJECT && b.entityType === EntityType.FUND) return 1;
    if (a.entityDetails.name < b.entityDetails.name) return -1;
    if (a.entityDetails.name > b.entityDetails.name) return 1;
    return 0;
  });
};
